import { supabase } from '@/lib/supabase';
import {
  provisionCoreForMerchant,
  createCoreLocation,
  createCoreProfessional,
  getOrganizationByCommerce,
  createCoreRole,
  type CoreOrganization,
} from '@/lib/zifekCore';
import { upsertBusinessProfile } from '@/lib/coreBusinessProfile';
import { enableModule } from '@/lib/coreModules';

// ─────────────────────────────────────────────────────────────
// ZIFEK CORE — 51. ONBOARDING
// Permet à un nouvel entrepreneur de créer son activité facilement, par un
// parcours guidé :
//
//   Créer compte → Choisir activité → Nom entreprise → Ville →
//   Services / produits → Horaires → Logo / photos → Coordonnées →
//   Création automatique du Business → Premier établissement →
//   Profil professionnel → Activation des modules nécessaires.
//
// On n'oblige JAMAIS l'utilisateur à tout configurer dès le début : chaque
// étape est optionnelle, la progression est sauvegardée et reprenable.
// ─────────────────────────────────────────────────────────────

export interface OnboardingStepDef {
  key: string;
  label: string;
  icon: string;
  desc: string;
  optional?: boolean;
}

/** Parcours d'onboarding (spec 51). */
export const ONBOARDING_STEPS: OnboardingStepDef[] = [
  { key: 'account', label: 'Créer son compte', icon: 'ri-user-add-line', desc: 'Identité et accès à la plateforme.' },
  { key: 'activity', label: 'Choisir l’activité', icon: 'ri-briefcase-4-line', desc: 'Secteur et catégorie d’activité.' },
  { key: 'business_name', label: 'Nom de l’entreprise', icon: 'ri-building-2-line', desc: 'Nom commercial et légal.' },
  { key: 'city', label: 'Ville', icon: 'ri-map-pin-line', desc: 'Localisation principale.' },
  { key: 'catalog', label: 'Services / produits', icon: 'ri-shopping-bag-3-line', desc: 'Premiers éléments de votre catalogue.', optional: true },
  { key: 'hours', label: 'Horaires', icon: 'ri-time-line', desc: 'Jours et heures d’ouverture.', optional: true },
  { key: 'media', label: 'Logo / photos', icon: 'ri-image-2-line', desc: 'Votre identité visuelle.', optional: true },
  { key: 'contact', label: 'Coordonnées', icon: 'ri-contacts-line', desc: 'Téléphone, WhatsApp, réseaux sociaux.', optional: true },
];

export interface CoreOnboarding {
  id: number;
  tenant_id: number | null;
  organization_id: number | null;
  current_step: string | null;
  completed_steps: string[] | null;
  business_created: boolean | null;
  location_created: boolean | null;
  professional_created: boolean | null;
  activated_modules: string[] | null;
  data: Record<string, unknown> | null;
  status: string | null;
  created_at: string | null;
  updated_at: string | null;
}

/** Modules activables à la fin du parcours (activation à la carte). */
export const ONBOARDING_MODULES: { key: string; label: string; icon: string; desc: string }[] = [
  { key: 'products', label: 'Boutique / Produits', icon: 'ri-store-3-line', desc: 'Vendre des produits en ligne.' },
  { key: 'services', label: 'Services & rendez-vous', icon: 'ri-service-line', desc: 'Prendre des rendez-vous.' },
  { key: 'hebergement', label: 'Hébergement', icon: 'ri-hotel-line', desc: 'Gérer chambres et réservations.' },
  { key: 'finance', label: 'Finance & facturation', icon: 'ri-funds-line', desc: 'Factures, devis, encaissements.' },
  { key: 'credit', label: 'Crédit / Carnet', icon: 'ri-book-2-line', desc: 'Suivre les créances clients.' },
  { key: 'crm', label: 'Clients (CRM)', icon: 'ri-user-star-line', desc: 'Fiches clients et timeline.' },
];

/**
 * Types d'activité proposés à l'onboarding. Chaque type est rattaché à un
 * profil de modules (freelance / hôtel / boutique) et à une liste de modules
 * recommandés, activés automatiquement quand l'entrepreneur choisit son activité.
 */
export interface ActivityTypeDef {
  key: string;
  label: string;
  icon: string;
  profile: 'freelance' | 'hotel' | 'shop';
  modules: string[];
  description: string;
}

export const ACTIVITY_TYPES: ActivityTypeDef[] = [
  { key: 'commerce_detail', label: 'Commerce de détail', icon: 'ri-store-3-line', profile: 'shop', modules: ['products', 'finance', 'crm'], description: 'Boutique, vente de produits, caisse.' },
  { key: 'restauration', label: 'Restauration', icon: 'ri-restaurant-2-line', profile: 'shop', modules: ['products', 'finance'], description: 'Café, restaurant, menu et commandes.' },
  { key: 'hebergement', label: 'Hébergement', icon: 'ri-hotel-line', profile: 'hotel', modules: ['hebergement', 'finance'], description: 'Hôtel, chambres, réservations.' },
  { key: 'beaute', label: 'Beauté & bien-être', icon: 'ri-scissors-2-line', profile: 'freelance', modules: ['services', 'crm', 'finance'], description: 'Salon, coiffure, soins sur rendez-vous.' },
  { key: 'sante', label: 'Santé', icon: 'ri-heart-pulse-line', profile: 'freelance', modules: ['services', 'crm', 'finance'], description: 'Cabinet, consultations sur rendez-vous.' },
  { key: 'professionnel', label: 'Services professionnels', icon: 'ri-briefcase-4-line', profile: 'freelance', modules: ['services', 'crm', 'finance', 'credit'], description: 'Conseil, freelance, prestations.' },
  { key: 'artisanat', label: 'Artisanat', icon: 'ri-hammer-line', profile: 'shop', modules: ['products', 'finance'], description: 'Fabrication et vente artisanale.' },
  { key: 'education', label: 'Éducation / Formation', icon: 'ri-book-open-line', profile: 'freelance', modules: ['services', 'crm', 'finance'], description: 'Cours, formations, inscriptions.' },
  { key: 'mixte', label: 'Mixte (produits + services)', icon: 'ri-apps-2-line', profile: 'shop', modules: ['products', 'services', 'finance', 'crm'], description: 'Vente de produits et prestations.' },
  { key: 'autre', label: 'Autre', icon: 'ri-more-line', profile: 'freelance', modules: ['finance', 'crm'], description: 'Activité personnalisée.' },
];

export function activityTypeMeta(key: string | null | undefined): ActivityTypeDef {
  return ACTIVITY_TYPES.find((a) => a.key === key) || ACTIVITY_TYPES[ACTIVITY_TYPES.length - 1];
}

/** Modules recommandés (clés d'onboarding) pour un type d'activité. */
export function recommendedModulesForActivity(key: string | null | undefined): string[] {
  return activityTypeMeta(key).modules;
}

/** Correspondance module d'onboarding → modules du registre Core (spec 61). */
export const ONBOARDING_MODULE_TO_CORE: Record<string, string[]> = {
  products: ['commerce', 'inventory'],
  services: ['agenda', 'booking'],
  hebergement: ['hospitality', 'booking'],
  finance: ['finance'],
  credit: ['finance'],
  crm: ['crm'],
};

export function onboardingStepMeta(key: string | null | undefined) {
  return ONBOARDING_STEPS.find((s) => s.key === key) || ONBOARDING_STEPS[0];
}

export async function getOnboarding(organizationId: number): Promise<CoreOnboarding | null> {
  const { data, error } = await supabase
    .from('core_onboarding')
    .select('*')
    .eq('organization_id', organizationId)
    .maybeSingle();
  if (error) throw error;
  return (data as CoreOnboarding) || null;
}

async function createOnboarding(organizationId: number, tenantId: number | null): Promise<CoreOnboarding | null> {
  const { data, error } = await supabase
    .from('core_onboarding')
    .insert({
      tenant_id: tenantId,
      organization_id: organizationId,
      current_step: 'account',
      completed_steps: [],
      activated_modules: [],
      data: {},
      status: 'in_progress',
    })
    .select('*')
    .maybeSingle();
  if (error) throw error;
  return (data as CoreOnboarding) || null;
}

/** Récupère (ou crée) la progression d'onboarding de l'organisation. */
export async function ensureOnboarding(organizationId: number, tenantId: number | null = null): Promise<CoreOnboarding | null> {
  const existing = await getOnboarding(organizationId);
  if (existing) return existing;
  return createOnboarding(organizationId, tenantId);
}

export interface OnboardingStepInput {
  organizationId: number;
  tenantId?: number | null;
  stepKey: string;
  data?: Record<string, unknown>;
}

/** Marque une étape comme terminée et avance le curseur. */
export async function completeOnboardingStep(input: OnboardingStepInput): Promise<CoreOnboarding | null> {
  const current = await ensureOnboarding(input.organizationId, input.tenantId ?? null);
  const completed = new Set(current?.completed_steps || []);
  completed.add(input.stepKey);

  const order = ONBOARDING_STEPS.map((s) => s.key);
  const nextStep = order.find((k) => !completed.has(k)) || 'done';

  const mergedData = { ...(current?.data || {}), ...(input.data || {}) };

  const { data, error } = await supabase
    .from('core_onboarding')
    .update({
      completed_steps: Array.from(completed),
      current_step: nextStep,
      data: mergedData,
      tenant_id: input.tenantId ?? current?.tenant_id ?? null,
      updated_at: new Date().toISOString(),
    })
    .eq('organization_id', input.organizationId)
    .select('*')
    .maybeSingle();
  if (error) throw error;
  return (data as CoreOnboarding) || null;
}

export async function toggleOnboardingModule(organizationId: number, moduleKey: string, active: boolean): Promise<void> {
  const current = await ensureOnboarding(organizationId);
  const set = new Set(current?.activated_modules || []);
  if (active) set.add(moduleKey);
  else set.delete(moduleKey);
  const { error } = await supabase
    .from('core_onboarding')
    .update({ activated_modules: Array.from(set), updated_at: new Date().toISOString() })
    .eq('organization_id', organizationId);
  if (error) throw error;
}

export interface FinalizeOnboardingInput {
  /** Id du marchand (users.id / commerce id) pour le provisionnement. */
  commerceId: number;
  organizationId: number;
  commercialName: string;
  businessType?: string | null;
  activityCategory?: string | null;
  city?: string | null;
  country?: string | null;
  address?: string | null;
  phone?: string | null;
  whatsapp?: string | null;
  email?: string | null;
  currency?: string | null;
  language?: string | null;
  logo?: string | null;
  description?: string | null;
  displayName?: string | null;
  profession?: string | null;
  modules?: string[];
}

export interface FinalizeOnboardingResult {
  tenantId: number | null;
  organizationId: number | null;
  locationId: number | null;
  professionalId: number | null;
  activatedModules: string[];
  steps: string[];
}

/**
 * Finalise l'onboarding : crée automatiquement le Business, le premier
 * établissement, le profil professionnel, puis active les modules choisis.
 * Idempotent et best-effort : une brique en échec n'annule pas les autres.
 */
export async function finalizeOnboarding(input: FinalizeOnboardingInput): Promise<FinalizeOnboardingResult> {
  const result: FinalizeOnboardingResult = {
    tenantId: null,
    organizationId: input.organizationId,
    locationId: null,
    professionalId: null,
    activatedModules: input.modules || [],
    steps: [],
  };

  // 1. Business (tenant + organisation + établissement + membership) — idempotent.
  try {
    const provisioned = await provisionCoreForMerchant({
      userId: input.commerceId,
      commercialName: input.commercialName,
      displayName: input.displayName || input.commercialName,
      businessType: input.businessType || null,
      activityCategory: input.activityCategory || null,
      email: input.email || null,
      phone: input.phone || null,
      whatsapp: input.whatsapp || null,
      logo: input.logo || null,
      description: input.description || null,
      address: input.address || null,
      city: input.city || null,
      country: input.country || null,
      currency: input.currency || null,
      language: input.language || null,
    });
    result.tenantId = provisioned.tenantId;
    if (provisioned.organizationId) result.organizationId = provisioned.organizationId;
    result.steps.push('business_created');
  } catch {
    /* best-effort */
  }

  let org: CoreOrganization | null = null;
  try {
    org = await getOrganizationByCommerce(input.commerceId);
  } catch {
    org = null;
  }
  const orgId = org?.id ?? result.organizationId ?? input.organizationId;

  // 2. Premier établissement.
  try {
    const locationId = await createCoreLocation({
      tenantId: org?.tenant_id ?? null,
      organizationId: orgId,
      name: input.commercialName,
      phone: input.phone || null,
      isPrimary: true,
      address: {
        line1: input.address || undefined,
        city: input.city || undefined,
        country: input.country || undefined,
      },
    });
    result.locationId = locationId;
    if (locationId) result.steps.push('location_created');
  } catch {
    /* best-effort */
  }

  // 3. Profil professionnel.
  try {
    const professionalId = await createCoreProfessional({
      tenantId: org?.tenant_id ?? null,
      organizationId: orgId,
      displayName: input.displayName || input.commercialName,
      profession: input.profession || input.businessType || null,
      verificationStatus: 'unverified',
      publicProfile: true,
    });
    result.professionalId = professionalId;
    if (professionalId) result.steps.push('professional_created');
  } catch {
    /* best-effort */
  }

  // 4. Profil public de l'entreprise (spec 52).
  try {
    await upsertBusinessProfile({
      organizationId: orgId,
      tenantId: org?.tenant_id ?? null,
      name: input.commercialName,
      logo: input.logo || null,
      description: input.description || null,
      category: input.activityCategory || input.businessType || null,
      city: input.city || null,
      country: input.country || null,
      address: input.address || null,
      phone: input.phone || null,
      whatsapp: input.whatsapp || null,
      email: input.email || null,
    });
  } catch {
    /* best-effort */
  }

  // 5. Rôle personnalisé « Manager » (base d'équipe) — best-effort.
  try {
    await createCoreRole({
      tenantId: org?.tenant_id ?? null,
      organizationId: orgId,
      name: 'Manager',
      description: 'Rôle créé automatiquement à l’onboarding.',
      permissions: ['dashboard.view', 'products.read', 'services.read', 'orders.read', 'customers.read'],
    });
  } catch {
    /* best-effort */
  }

  // 6. Activation des modules recommandés dans le registre du socle (spec 59/61).
  try {
    const coreKeys = new Set<string>(['core']);
    (input.modules || []).forEach((m) => (ONBOARDING_MODULE_TO_CORE[m] || []).forEach((k) => coreKeys.add(k)));
    for (const key of coreKeys) {
      try {
        await enableModule({ tenantId: org?.tenant_id ?? null, organizationId: orgId, moduleKey: key });
      } catch {
        /* un module en échec n'annule pas les autres */
      }
    }
  } catch {
    /* best-effort */
  }

  // 7. Finalisation.
  try {
    await ensureOnboarding(orgId, org?.tenant_id ?? null);
    await supabase
      .from('core_onboarding')
      .update({
        tenant_id: org?.tenant_id ?? null,
        organization_id: orgId,
        business_created: true,
        location_created: !!result.locationId,
        professional_created: !!result.professionalId,
        activated_modules: input.modules || [],
        completed_steps: ONBOARDING_STEPS.map((s) => s.key),
        current_step: 'done',
        status: 'completed',
        updated_at: new Date().toISOString(),
      })
      .eq('organization_id', orgId);
  } catch {
    /* best-effort */
  }

  result.organizationId = orgId;
  return result;
}